import {
  type AnyBlockType,
  BlockType,
  ObsoleteBlockType,
  SpecialBlockType,
  UnknownBlockType,
} from '../../base/baseSchema.js';

export enum SkillV2BlockTypeCompatibility {
  /**
   * Examples: Start block, Output block
   */
  Required = 'Required',
  /**
   * Examples: Script block, LLM block
   */
  Allowed = 'Allowed',
  /**
   * Some blocks can be used in Skill v2 flows only under certain conditions.
   * Examples:
   * - REST block using public APIs without authentication are allowed and commonly used in Skills.
   * - Human Task block with no categoryId is allowed.
   */
  ConditionallyAllowed = 'ConditionallyAllowed',
  /**
   * Some blocks are not cross organization compatible and thus cannot be used in Skill v2 flows.
   * Examples:
   * - SubFlow block can have any block inside it, and therefore cannot guarantee cross-organization compatibility.
   * - GetNotableObject block relies on organization-specific Notable Object definitions.
   */
  NotAllowed = 'NotAllowed',
}

/**
 * Transcribed from https://notable-health.atlassian.net/wiki/spaces/notableengineering/pages/3667722258/Skill-ready+blocks
 * as of 2025-12-10
 */
export const SKILL_V2_BLOCK_TYPE_COMPATIBILITY: Record<
  AnyBlockType,
  SkillV2BlockTypeCompatibility
> = {
  [BlockType.Start]: SkillV2BlockTypeCompatibility.Required,
  [BlockType.Output]: SkillV2BlockTypeCompatibility.Required,
  [BlockType.SubFlowV2]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.SubFlowV1]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.IteratorV1]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.IteratorV2]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.Repeat]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.Conditional]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.Switch]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.ScriptedFlow]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.Text]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.FilterList]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.Wait]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.WaitV2]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.UpdateFlowConfig]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.LlmPrompt]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.Evaluation]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.MedicalNecessityCheck]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.GetNotableObject]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.Fhir]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.AuthorizationSubmission]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.Ocr]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.OcrV2]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.InformationExtraction]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.VoiceCall]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.VoiceCallV2]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.VoiceTranscribe]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.HieQueryDocuments]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.HieGetDocuments]: SkillV2BlockTypeCompatibility.ConditionallyAllowed,
  [BlockType.WaitForFormCompletion]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.WaitForAvailableSchedulingSlot]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.Conversation]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.AdvancedFhir]: SkillV2BlockTypeCompatibility.ConditionallyAllowed,
  [BlockType.HCCAlgorithmBlock]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.DocumentTemplate]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.GenerateFile]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.PatientDataStore]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.Rest]: SkillV2BlockTypeCompatibility.ConditionallyAllowed,
  [BlockType.Form]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.GetFormSet]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.GetInsurances]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.SendEmail]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.SendSms]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.Fax]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.HumanTask]: SkillV2BlockTypeCompatibility.ConditionallyAllowed,
  [BlockType.UpdateNotableObject]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.Hl7]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.Mirth]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.RealTimeEligibility]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.InsuranceCard]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.FileConversion]: SkillV2BlockTypeCompatibility.Allowed,
  [BlockType.WaitForSignal]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.Callback]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.CareGapAlgorithmBlock]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.DocumentExtract]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.GetExternalDocumentPacket]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.GetPatient]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.GetPatientOrder]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.GetProvider]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.ExportExternalDocumentPacket]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.ExportTextToBucket]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.CreateEncounterSummary]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.CreatePatientOrder]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.AsynchronousExportJob]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.CreateNotableObject]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.DocumentSearch]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.GetSftpFile]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.ServiceReview]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.SendToLabelboxForReview]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.RestApiClassicAuthorization]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.Scheduling]: SkillV2BlockTypeCompatibility.NotAllowed,
  [BlockType.AsynchronousExtractJob]: SkillV2BlockTypeCompatibility.NotAllowed,
  [ObsoleteBlockType.ObsoleteApiSubmissions]: SkillV2BlockTypeCompatibility.NotAllowed,
  [ObsoleteBlockType.ObsoleteGetEncounterSummary]: SkillV2BlockTypeCompatibility.NotAllowed,
  [ObsoleteBlockType.ObsoleteHumanReview]: SkillV2BlockTypeCompatibility.NotAllowed,
  [ObsoleteBlockType.ObsoleteSkill]: SkillV2BlockTypeCompatibility.NotAllowed,
  [UnknownBlockType.Unknown]: SkillV2BlockTypeCompatibility.NotAllowed,
  [SpecialBlockType.SkillV2]: SkillV2BlockTypeCompatibility.Allowed,
};

export const SKILL_V2_REQUIRED_BLOCK_TYPES = new Set<AnyBlockType>();
for (const [blockType, compatibility] of Object.entries(SKILL_V2_BLOCK_TYPE_COMPATIBILITY)) {
  if (compatibility === SkillV2BlockTypeCompatibility.Required) {
    SKILL_V2_REQUIRED_BLOCK_TYPES.add(blockType as AnyBlockType);
  }
}
